import api from "../../../utils/api";
import { unwrap } from "./apiEnvelope";

// ============================================================================
// Economía del embudo (#1291, v2 #1352) — servicio de la consola.
//   GET /crm/economics?from=YYYY-MM&to=YYYY-MM[&pipeline=<id>|all][&refresh=1]
//   PUT /crm/economics/settings  { stageKinds: { <id de etapa>: clase | null } }
// El análisis lo arma el backend (modules/crmEconomics); aquí solo se tipa y
// se desenvuelve la respuesta. 403 = el usuario no ve el dinero del equipo.
// ============================================================================

export type BucketId = "ads" | "platforms" | "marketingTeam" | "salesTeam" | "otherMarketing";
export type BucketAmounts = Record<BucketId, number>;
export type StageClass = "open" | "won" | "lost" | "parked";
export type RowKind = "lead" | "deal" | "open" | "won";

export interface CostLine {
  name: string;
  amount: number;
  count: number;
  /** expense = gasto; paid = pago real de nómina; estimate = estimación del padrón; commission. */
  kind: "expense" | "paid" | "estimate" | "commission";
}

export interface CostBucket {
  id: BucketId;
  label: string;
  hint: string;
  amount: number;
  share: number | null;
  lines: CostLine[];
}

export interface MonthlyCost extends BucketAmounts {
  period: string;
  total: number;
}

export interface TeamMemberCost {
  id: string | null;
  name: string;
  role: string;
  bucket: BucketId;
  paid: number;
  estimated: number;
  total: number;
  paidMonths: number;
  estimatedMonths: number;
  monthlyEstimate: number | null;
  since: string | null;
}

export interface UnitCost {
  id: string;
  label: string;
  units: number;
  total: number | null;
  byBucket: BucketAmounts | null;
}

export interface FunnelStage {
  id: string;
  label: string;
  kind: RowKind;
  reached: number;
  current: number | null;
  decided?: number;
  wonFromHere?: number;
  costPerUnit: number | null;
  costByBucket: BucketAmounts | null;
  conversionFromPrev: number | null;
  pClose: number | null;
  pHubspot: number | null;
  expectedCloseCost: number | null;
  medianDays: number | null;
  durationSamples?: number;
  openAmount: number;
  expectedValue: number;
  lostHere: number;
  parkedHere: number;
  isProposal?: boolean;
  isDemo?: boolean;
}

export interface Outcome {
  deals: number;
  amount: number;
}

export interface Allocation {
  kind: StageClass;
  label: string;
  deals: number;
  cost: number;
  share: number;
}

export interface Cohort extends BucketAmounts {
  period: string;
  leads: number;
  demos: number;
  notes: number;
  deals: number;
  proposals: number | null;
  won: number;
  lost: number;
  parked: number;
  active: number;
  cost: number;
  costPerLead: number | null;
  costPerDemo: number | null;
  costPerDeal: number | null;
}

export interface PipelineRow {
  id: string;
  label: string;
  deals: number;
  active: number;
  parked: number;
  lost: number;
  won: number;
  untouched: number;
  winRate: number | null;
  lossRate: number | null;
}

export interface LeadSource {
  source: string;
  label: string;
  leads: number;
  qualified: number;
  converted: number;
  lost: number;
  share: number | null;
  conversion: number | null;
}

export interface DealRow {
  id: string;
  hubspotId: string | null;
  title: string;
  company: string | null;
  contact: string | null;
  amount: number;
  pipelineLabel: string | null;
  stageLabel: string;
  outcome: StageClass;
  createdAt: string | null;
  daysOpen: number | null;
  daysInStage: number | null;
  exactHistory: boolean;
  documents: number;
  documentIds: string[];
}

export interface DocumentLink {
  type: "deal" | "company" | "contact";
  id: string;
  name: string | null;
  localId?: string | null;
}

export interface HubspotDocument {
  id: string;
  name: string;
  extension: string | null;
  type: string | null;
  size: number | null;
  url: string | null;
  createdAt: string | null;
  linked: DocumentLink[];
  isPdf: boolean;
  onlyReferenced?: boolean;
  note?: string;
}

export type InsightSeverity = "high" | "medium" | "low" | "info";

export interface Insight {
  severity: InsightSeverity;
  area: string;
  title: string;
  body: string;
  metric: string | null;
  action: string | null;
}

export interface DataQualityItem {
  id: string;
  label: string;
  value: number | null;
  detail: string;
  status: "ok" | "warn" | "bad";
  fix: string;
}

export interface StageDefinition {
  id: string;
  label: string;
  pipelineId: string | null;
  pipelineLabel: string | null;
  kind: StageClass;
  order: number;
  probability: number | null;
  overridden: boolean;
}

export interface EconomicsKpis {
  totalCost: number;
  leads: number;
  organicLeads: number;
  demos: number;
  deals: number;
  proposals: number | null;
  won: number;
  lost: number;
  parked: number;
  active: number;
  wonAmount: number;
  openAmount: number;
  expectedPipelineValue: number;
  costPerLead: number | null;
  costPerOrganicLead: number | null;
  costPerDemo: number | null;
  costPerDeal: number | null;
  costPerProposal: number | null;
  cac: number | null;
  leadToDeal: number | null;
  dealToProposal: number | null;
  dealToWin: number | null;
  winRate: number | null;
  avgTicket: number | null;
  roi: number | null;
  medianDaysToClose: number | null;
  monthlyAverageCost: number | null;
}

export interface EconomicsReport {
  companyId: string | null;
  period: { from: string; to: string; months: string[] };
  currency: string;
  generatedAt: string;
  summary: {
    headline: string;
    situation: string;
    complication: string;
    resolution: string[];
    costPerLead: number | null;
    biggestBucket: { id: BucketId; label: string; share: number | null } | null;
    wonDeals: number;
  };
  kpis: EconomicsKpis;
  costs: {
    total: number;
    buckets: CostBucket[];
    byBucket: BucketAmounts;
    monthly: MonthlyCost[];
    team: TeamMemberCost[];
    expensesCount: number;
    monthsWithCost: number;
    monthlyAverage: number | null;
    withoutRate: { currency: string; amount: number }[];
  };
  unitCosts: UnitCost[];
  leads: { total: number; fromContacts: number; fromForms: number; bulk: number; organic: number; bulkDays: { date: string; count: number }[]; sources: LeadSource[] };
  activity: { monthly: { period: string; demos: number; notes: number; tasksDone: number }[]; demos: number; notes: number; tasksDone: number };
  funnel: {
    source: "hubspot" | "deals" | "local";
    mode: "all" | "single" | "local";
    pipelineId: string | null;
    pipelineLabel: string;
    pipelines: { id: string; label: string; deals: number }[];
    exactHistory: number;
    stages: FunnelStage[];
    outcomes: { active: Outcome; parked: Outcome; lost: Outcome; won: Outcome };
  };
  allocation: Allocation[];
  cohorts: Cohort[];
  pipelines: PipelineRow[];
  breakEven: {
    monthlyCost: number | null;
    avgTicket: number | null;
    ticketSamples: number;
    winsPerMonth: number | null;
    dealsPerMonth: number | null;
    requiredWinRate: number | null;
    currentWinRate: number | null;
    ticketNeeded: { wins: number; ticket: number | null }[];
  };
  installedBase: { count: number; withBilling: number; names: string[] };
  deals: DealRow[];
  documents: HubspotDocument[];
  documentsSummary: { total: number; pdf: number; linkedToDeals: number; withoutFile: number };
  dataQuality: { items: DataQualityItem[]; score: number };
  insights: Insight[];
  stageDefinitions: StageDefinition[];
  hubspot: { connected: boolean; cachedAt: string | null; errors: Record<string, string>; pipelinesRead: number };
  assumptions: string[];
}

export interface EconomicsQuery {
  from: string;
  to: string;
  pipeline?: string | null;
  refresh?: boolean;
}

export async function fetchEconomics(q: EconomicsQuery): Promise<EconomicsReport> {
  const res = await api.get("/crm/economics", {
    params: {
      from: q.from,
      to: q.to,
      ...(q.pipeline ? { pipeline: q.pipeline } : {}),
      ...(q.refresh ? { refresh: 1 } : {}),
    },
    // Con refresh el backend recorre HubSpot completo: dale tiempo.
    timeout: q.refresh ? 290000 : 120000,
  });
  return unwrap<EconomicsReport>(res);
}

/** Fija la clasificación de una o más etapas; `null` la regresa a automática. */
export async function saveStageKinds(stageKinds: Record<string, StageClass | null>): Promise<Record<string, StageClass>> {
  const res = await api.put("/crm/economics/settings", { stageKinds });
  return unwrap<{ stageKinds: Record<string, StageClass> }>(res).stageKinds;
}

/** ¿El error del API es «no tienes acceso»? */
export function isForbidden(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 403;
}
